import { showUserAlert } from "../utils/userErrorMessage";
// CarInfo.jsx
import React, { useEffect, useMemo, useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { supabase } from "../supabase/client";
import Header from "../Component/Header";
import { useNavigate, Link } from "react-router-dom";

export default function CarInfo() {
  const [filters, setFilters] = useState({ 차량번호: "", 기사명: "" });
  const [carData, setCarData] = useState([]);
  const [manageDraft, setManageDraft] = useState({}); // { [id]: { direct, karaoke_machine, electronic_display } }
  const [sortOption, setSortOption] = useState("car_number");
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();

  const fetchData = async () => {
    const email = localStorage.getItem("email");
    if (!email) return;

    const { data, error } = await supabase
      .from("cars")
      .select("*")
      .eq("company", email)
      .order("car_number", { ascending: true });

    if (error) {
      console.error("차량 정보 가져오기 오류:", error.message);
      setCarData([]);
    } else {
      setCarData(data);
      const init = {};
      data.forEach(r => {
        init[r.id] = {
          direct: !!r.direct,
          karaoke_machine: !!r.karaoke_machine,
          electronic_display: !!r.electronic_display,
        };
      });
      setManageDraft(init);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const toggleDraft = (id, field) => {
    setManageDraft(prev => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        [field]: !prev[id]?.[field],
      },
    }));
  };

  const visibleCars = useMemo(() => {
    const filtered = carData.filter((row) => (
      (!filters.차량번호 || row.car_number?.includes(filters.차량번호)) &&
      (!filters.기사명 || row.driver?.includes(filters.기사명))
    ));

    return [...filtered].sort((a, b) => {
      let result = 0;
      if (sortOption === "car_number") {
        result = String(a.car_number || "").localeCompare(String(b.car_number || ""), "ko", { numeric: true });
      } else if (sortOption === "year") {
        result = (Number(a.year) || 0) - (Number(b.year) || 0);
      } else if (sortOption === "seat_detail") {
        result = (Number(String(a.seat_detail || "").replace(/\D/g, "")) || 0) - (Number(String(b.seat_detail || "").replace(/\D/g, "")) || 0);
      } else if (sortOption === "karaoke_machine") {
        result = (manageDraft[a.id]?.karaoke_machine ? 1 : 0) - (manageDraft[b.id]?.karaoke_machine ? 1 : 0);
      } else if (sortOption === "electronic_display") {
        result = (manageDraft[a.id]?.electronic_display ? 1 : 0) - (manageDraft[b.id]?.electronic_display ? 1 : 0);
      }
      if (result !== 0) return -result;
      return String(a.car_number || "").localeCompare(String(b.car_number || ""), "ko", { numeric: true });
    });
  }, [carData, filters, manageDraft, sortOption]);

  const handleFilter = () => {};

  const formatSeat = (row) => {
    const seat = String(row.seat || "").trim();
    const detail = String(row.seat_detail || "").trim();
    if (!seat) return detail ? `${detail}인승` : "";
    if (!detail) return seat;
    const detailText = detail.endsWith("인승") ? detail : `${detail}인승`;
    return `${seat}(${detailText})`;
  };

  const handleSaveDirect = async () => {
    if (saving) return;
    // 변경된 행만 추출
    const changes = carData
      .map(row => {
        const draft = manageDraft[row.id] || {};
        return {
          id: row.id,
          direct: !!draft.direct,
          karaoke_machine: !!draft.karaoke_machine,
          electronic_display: !!draft.electronic_display,
          changed:
            !!draft.direct !== !!row.direct ||
            !!draft.karaoke_machine !== !!row.karaoke_machine ||
            !!draft.electronic_display !== !!row.electronic_display,
        };
      })
      .filter(row => row.changed);

    if (changes.length === 0) {
      showUserAlert("변경된 항목이 없습니다.");
      return;
    }

    if (!window.confirm(`${changes.length}건 변경 사항을 저장할까요?`)) return;

    setSaving(true);
    try {
      // 개별 업데이트(안전): 권한/정책 이슈 피하기 좋음
      const company = localStorage.getItem("email") || "";
      for (const change of changes) {
        const { id, changed, ...updateData } = change;
        const { error } = await supabase
          .from("cars")
          .update(updateData)
          .eq("id", id)
          .eq("company", company);
        if (error) throw error;
      }
      showUserAlert("저장되었습니다.");
      // 다시 조회해서 화면/초깃값 동기화
      await fetchData();
    } catch (e) {
      showUserAlert("저장 실패: " + (e.message || e));
    } finally {
      setSaving(false);
    }
  };


  return (
    <div className="space-y-1">
      <style>{`
        .car-info-table th,
        .car-info-table td {
          text-align: center;
        }

        .car-info-table .col-seq {
          width: 40px;
          min-width: 40px;
          white-space: nowrap;
        }

        .car-info-table .col-car-number {
          width: 120px;
          min-width: 120px;
          white-space: nowrap;
        }

        .car-info-table .col-driver {
          width: 76px;
          min-width: 76px;
          white-space: nowrap;
          word-break: keep-all;
        }
      `}</style>
      <Header />

      <div className="px-4 pt-1 pb-4">
        <h2 className="text-lg font-semibold">차량 정보</h2>

        <div className="flex flex-wrap gap-2 items-end">
          <Input
            placeholder="차량번호"
            value={filters.차량번호}
            onChange={(e) => setFilters({ ...filters, 차량번호: e.target.value })}
            className="w-40"
          />
          <Input
            placeholder="기사명"
            value={filters.기사명}
            onChange={(e) => setFilters({ ...filters, 기사명: e.target.value })}
            className="w-40"
          />
          <Button onClick={handleFilter}>검색</Button>
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-700">정렬</span>
            <select
              className="h-10 rounded border border-gray-300 bg-white px-3 text-sm"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="car_number">차량번호</option>
              <option value="year">연식</option>
              <option value="seat_detail">승차인원</option>
              <option value="karaoke_machine">노래방기기</option>
              <option value="electronic_display">전광판</option>
            </select>
          </div>
        </div>

        <Card>
          <CardContent className="bg-white">
            <div className="max-h-[70vh] overflow-auto">
              <table className="car-info-table table-auto w-full border border-gray-300 text-sm">
                <thead className="sticky top-0 z-30 bg-blue-500 text-white">
                  <tr>
                    {[
                      "순번",
                      "차량번호",
                      "차명",
                      "기사명",
                      "년식",
                      "승차인원",
                      "노래방기기",
                      "전광판",
                      "직접관리"
                    ].map((header) => (
                      <th
                        key={header}
                        className={
                          "sticky top-0 z-30 border border-gray-300 bg-blue-500 p-2 " +
                          (header === "순번" ? "col-seq " : "") +
                          (header === "차량번호" ? "col-car-number " : "") +
                          (header === "기사명" ? "col-driver " : "")
                        }
                      >
                        {header}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visibleCars.map((row, index) => (
                    <tr key={row.id}>
                      <td className="border border-gray-300 p-2 col-seq">
                        {index + 1}
                      </td>
                      <td className="border border-gray-300 p-2 col-car-number">
                        <Link
                          to={`/car-edit/${row.id}`}
                          className="text-blue-600 underline hover:text-blue-800"
                        >
                          {row.car_number}
                        </Link>
                      </td>
                      <td className="border border-gray-300 p-2">
                        {row.car_name}
                      </td>
                      <td className="border border-gray-300 p-2 col-driver">
                        {row.driver}
                      </td>
                      <td className="border border-gray-300 p-2">
                        {row.year}
                      </td>
                      <td className="border border-gray-300 p-2">
                        {formatSeat(row)}
                      </td>
                      <td className="border border-gray-300 p-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!manageDraft[row.id]?.karaoke_machine}
                          onChange={() => toggleDraft(row.id, "karaoke_machine")}
                          disabled={saving}
                        />
                      </td>
                      <td className="border border-gray-300 p-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!manageDraft[row.id]?.electronic_display}
                          onChange={() => toggleDraft(row.id, "electronic_display")}
                          disabled={saving}
                        />
                      </td>
                      <td className="border border-gray-300 p-2 text-center">
                        <input
                          type="checkbox"
                          checked={!!manageDraft[row.id]?.direct}
                          onChange={() => toggleDraft(row.id, "direct")}
                          disabled={saving}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
        <div className="flex gap-2 mt-2">
          <Button onClick={() => navigate("/car-add")}>추가</Button>
          <Button onClick={handleSaveDirect} disabled={saving}>
            {saving ? "저장 중..." : "관리 사항 저장"}
          </Button>
        </div>
      </div>
    </div>
  );
}
